import * as wjcCore from "@grapecity/wijmo"
import "@grapecity/wijmo.cultures/wijmo.culture.ja"
import "@grapecity/wijmo.styles/wijmo.css"
import React, { useEffect, useRef } from "react"
import { Route, BrowserRouter as Router, Switch } from "react-router-dom"
import { Id, toast, ToastContainer } from "react-toastify"
import Auth from "./Auth"
import Main from "./Main"
import "./scss/App.scss"

import { faCheck, faClock, faUser } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AsyncThunkAction } from "@reduxjs/toolkit"
import dayjs from "dayjs"
import "dayjs/locale/ja"
import { ErrorBoundary, FallbackProps } from "react-error-boundary"
import { useSelector } from "react-redux"
import Login from "./features/Login/Login"
import Settings from "./features/Settings/Settings"
import { TabletBody } from "./features/Tablet/TabletBody"
import { setAlerts } from "./globalStore/alertStore"
import { RootState } from "./globalStore/rootreducer"
import { TLoginUserInfo } from "./service/auth/TAuth"
import DakokuTenkoAlertService from "./service/kintai/dakokuTenkoAlert/DakokuTenkoAlertService"
import { TApiResponse } from "./service/TApiResponse"
import { getDakokuTenkoAlertConnection, TDakokuTenkoAlertPayload } from "./signalr/dakokuTenkoAlertClient"
import dateUtil from "./ts/util/dateUtil"

//dayjsのロケーション確定
dayjs.locale("ja")

// wijmoライセンス設定
process.env.REACT_APP_WIJMO_LICENSE_KEY && wjcCore.setLicenseKey(process.env.REACT_APP_WIJMO_LICENSE_KEY)

// チャンク読込エラー（デプロイ後の旧キャッシュ）を検知して自動リロード
const isChunkLoadError = (error: Error): boolean =>
  error.name === "ChunkLoadError" || /Loading chunk [\d]+ failed/.test(error.message) || /Loading CSS chunk [\d]+ failed/.test(error.message)

// エラーハンドリング用のラッパー
const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  React.useEffect(() => {
    if (isChunkLoadError(error)) {
      // チャンク読込エラーの場合は自動リロード（無限ループ防止付き）
      const key = "chunk_error_reload"
      const lastReload = sessionStorage.getItem(key)
      if (!lastReload || Date.now() - Number(lastReload) > 10000) {
        sessionStorage.setItem(key, String(Date.now()))
        window.location.reload()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div role="alert">
      <p>Error Message</p>
      <pre>{error!.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  )
}

function App() {
  const isTablet = useSelector((state: RootState) => state.terminal.isTablet)

  const shownAlertIdsRef = useRef<Set<number>>(new Set())
  // 打刻点呼アラートID → toastId の対応表
  const alertToastIdMapRef = useRef<Map<number, Id>>(new Map())
  const didFetchUnreadRef = useRef(false)
  const loginUserInfo = useSelector((state: RootState) => state.user.LoginUserInfo)
  // 前回のユーザIDを覚えておく
  const prevUserIdRef = useRef<number | null>(null)
  const fetchUnreadDakokuTenkoAlerts = async () => {
    // 未読打刻点呼アラートを取得する
    try {
      const service = new DakokuTenkoAlertService()
      const res: TApiResponse<TDakokuTenkoAlertPayload[]> = await service.getUnreadList()
      return res.data
    } catch (error) {
      disPatch(setAlerts(error))
    } finally {
    }
  }

  // ログインユーザが打刻監視担当かどうか
  const isDakokuWatcher = () => {
    const str = localStorage.getItem("loginUserInfo")
    if (!str) return false
    try {
      const info = JSON.parse(str) as TLoginUserInfo
      return info?.打刻監視担当フラグ === 1
    } catch {
      return false
    }
  }

  //打刻監視完了可能かどうか
  const isDakokuChecker = () => {
    const str = localStorage.getItem("loginUserInfo")
    if (!str) return false
    try {
      const info = JSON.parse(str) as TLoginUserInfo
      return info?.打刻監視変更可能フラグ === 1
    } catch {
      return false
    }
  }

  const markDakokuTenkoAlertAsRead = async (打刻点呼アラートID: number, 更新日時: string) => {
    // 打刻点呼アラートを既読化する
    try {
      const service = new DakokuTenkoAlertService()
      // 給与実績IDの有無で処理分岐
      await service.putMarkAsRead(打刻点呼アラートID, {
        打刻点呼アラートID,
        更新日時,
      })
    } catch (error) {
      disPatch(setAlerts(error))
    } finally {
    }
  }

  const showDakokuTenkoAlertToast = (payload: TDakokuTenkoAlertPayload) => {
    // 既に表示済みならスキップ
    if (shownAlertIdsRef.current.has(payload.打刻点呼アラートID)) return
    shownAlertIdsRef.current.add(payload.打刻点呼アラートID)

    // トースト表示
    const toastId = toast.error(
      <>
        <span style={{ display: "inline-block", minWidth: "6rem" }}>{payload.打刻点呼アラート区分名称1}</span>
        <span className="ms-4">【{payload.部所略称}】</span>
        <br />
        <span style={{ display: "inline-block", minWidth: "6rem" }}>
          <FontAwesomeIcon icon={faClock} className="me-2" />
          {dateUtil.format(payload.発生日時, "MM/DD HH:mm")}
        </span>
        <span className="ms-2">
          <FontAwesomeIcon icon={faUser} className="me-2" />
          <span style={{ display: "inline-block", minWidth: "4ch", textAlign: "right" }}>
            {String(payload.社員コード).padStart(4, " ")}
          </span>
          : {payload.社員氏名}
        </span>
      </>,
      {
        autoClose: false,
        closeOnClick: false,
        closeButton: ({ closeToast }) => {
          if (!isDakokuChecker()) {
            // 打刻監視変更可能でない場合、閉じるボタンを表示しない
            return <></>
          }
          return (
            <button
              type="button"
              className="w-1rem"
              onClick={async (e) => {
                e.stopPropagation()

                try {
                  await markDakokuTenkoAlertAsRead(payload.打刻点呼アラートID, payload.更新日時)
                } catch (err) {
                  console.error("既読化に失敗しました", err)
                  // 必要ならここで別トースト
                }
                closeToast(e) // トーストを閉じる
              }}>
              <FontAwesomeIcon icon={faCheck} />
            </button>
          )
        },
        draggable: false,
      },
    )
    // 後から閉じるために保持
    alertToastIdMapRef.current.set(payload.打刻点呼アラートID, toastId)
  }

  // 未読アラート一覧を「発生日時の昇順（古い順）」でトースト生成する共通処理。
  // ToastContainer は newestOnTop=true のため「最後に生成したトースト」が最上部に来る。
  // 古い順に生成すれば最新アラートが最後に生成され、最上部に表示される（KAIROS-1087）。
  // 初期取得・SignalR再接続時の再同期・動作確認用トリガーが同じ経路を通るよう共通化している。
  const showDakokuTenkoAlertsSorted = (list: TDakokuTenkoAlertPayload[]) => {
    const sorted = [...list].sort(
      (a, b) => dayjs(a.発生日時).valueOf() - dayjs(b.発生日時).valueOf(),
    )
    sorted.forEach((alert) => {
      showDakokuTenkoAlertToast(alert)
    })
  }

  //タブレットの場合最小サイズが変わるためここで定義する
  useEffect(() => {
    if (isTablet) {
      document.body.style.minWidth = "1280px"
    } else {
      document.body.style.minWidth = "1366px"
    }
  }, [isTablet])

  // ユーザ変更(ログイン/ログアウト/ユーザ切替)を検知してリセット
  useEffect(() => {
    const currentUserId = loginUserInfo?.社員ID ?? null

    // 初回 or ユーザが変わったとき
    if (prevUserIdRef.current !== currentUserId) {
      // 既読フラグ・表示済みIDをリセット
      didFetchUnreadRef.current = false
      shownAlertIdsRef.current.clear()
      // 画面上に残っているトーストもいったん全部消す
      toast.dismiss()

      prevUserIdRef.current = currentUserId
    }
  }, [loginUserInfo])
  useEffect(() => {
    // すでに一度「成功済み」なら何もしない
    if (didFetchUnreadRef.current) return

    // ログイン済み & 監視担当か？
    if (!isDakokuWatcher()) return
    ;(async () => {
      try {
        const list = await fetchUnreadDakokuTenkoAlerts()
        if (!list) {
          // データゼロでも「一応成功」とみなしてフラグ立てるならここで
          didFetchUnreadRef.current = true
          return
        }

        // 発生日時の昇順（古い順）で表示（KAIROS-1087）。詳細は showDakokuTenkoAlertsSorted 参照。
        showDakokuTenkoAlertsSorted(list)

        // ここで初めて「成功したからもう二度とやらない」フラグを立てる
        didFetchUnreadRef.current = true
      } catch (err) {
        console.error("未読アラート取得に失敗しました", err)
        // フラグは立てない → 次のレンダーで再チャレンジできる
      }
    })()
    //
  })
  // 依存配列なしでも didFetchUnreadRef で1回に制限

  // --- SignalR 打刻点呼アラート購読 ---
  useEffect(() => {
    const connection = getDakokuTenkoAlertConnection()

    const handleCreated = (alert: TDakokuTenkoAlertPayload) => {
      // 念のため、監視担当チェック（権限が後から変わる可能性に備えて）
      if (!isDakokuWatcher()) return
      showDakokuTenkoAlertToast(alert)
    }

    const handleRead = (alertId: number) => {
      const toastId = alertToastIdMapRef.current.get(alertId)
      if (toastId !== undefined) {
        toast.dismiss(toastId)
        alertToastIdMapRef.current.delete(alertId)
      }
      shownAlertIdsRef.current.delete(alertId)
    }

    // start() の前にイベントハンドラを登録して取りこぼしを防ぐ
    connection.on("DakokuTenkoAlertCreated", handleCreated)
    connection.on("DakokuTenkoAlertRead", handleRead)

    // 再接続時に未読リストを再同期
    const resyncUnread = async () => {
      if (!isDakokuWatcher()) return
      try {
        const list = await fetchUnreadDakokuTenkoAlerts()
        if (!list) return
        // 発生日時の昇順（古い順）で表示（KAIROS-1087）。詳細は showDakokuTenkoAlertsSorted 参照。
        showDakokuTenkoAlertsSorted(list)
      } catch (err) {
        console.error("[SignalR] 再接続時の未読再同期に失敗", err)
      }
    }

    connection.onreconnected(() => {
      console.info("[SignalR] DakokuTenkoAlertHub 再接続成功 → 未読再同期")
      resyncUnread()
    })

    let cancelled = false
    let started = false

    const tryStart = async () => {
      if (cancelled || started) return

      // まだログインしていない or 監視担当じゃないなら、5秒後に再チェック
      if (!isDakokuWatcher()) {
        setTimeout(tryStart, 5000)
        return
      }

      try {
        await connection.start()
        started = true
        console.info("[SignalR] DakokuTenkoAlertHub 接続成功")
      } catch (err) {
        console.error("[SignalR] 接続失敗 (再試行します)", err)
        // サーバがまだ起動していない／ネットワーク不調など → 5秒後にリトライ
        setTimeout(tryStart, 5000)
      }
    }

    // 初回キック
    tryStart()

    return () => {
      cancelled = true
      connection.off("DakokuTenkoAlertCreated", handleCreated)
      connection.off("DakokuTenkoAlertRead", handleRead)
      connection.stop().catch(() => {
        /* noop */
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- 動作確認用: 疑似点呼アラートを手動発火（開発ビルド限定） ---
  // ブラウザのコンソールから以下で呼び出す:
  //   window.fireTestTenkoAlerts(20)  … 疑似アラートを20件表示（引数省略時20件）
  //   window.clearTestTenkoAlerts()   … 表示中トーストを全消去
  // NODE_ENV=production では登録しないため、本番ビルドには一切含まれない。
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return

    const w = window as unknown as {
      fireTestTenkoAlerts?: (count?: number) => void
      clearTestTenkoAlerts?: () => void
    }

    // 疑似未読アラートを count 件生成し、本番と同じ表示経路(showDakokuTenkoAlertsSorted)へ通す。
    // i=0 が最新(発生日時=現在)、i=count-1 が最古。
    // 修正が効いていれば「テスト#00(最新)」が最上部、「#(count-1)(最古)」が最下部に並ぶ。
    w.fireTestTenkoAlerts = (count = 20) => {
      // 再実行のたびIDを一意にし、重複抑止(shownAlertIdsRef)で握り潰されないようにする
      const base = Date.now()
      const list: TDakokuTenkoAlertPayload[] = Array.from({ length: count }, (_, i) => ({
        打刻点呼アラートID: base + i,
        勤務日: dayjs().format("YYYY-MM-DD"),
        乗務員ID: 900000 + i,
        社員コード: String(1000 + i),
        社員氏名: `テスト乗務員${String(i).padStart(2, "0")}`,
        部所コード: "T01",
        部所名: "テスト部所",
        部所略称: "ﾃｽﾄ",
        打刻点呼アラート区分ID: 1,
        打刻点呼アラート区分コード: "01",
        打刻点呼アラート区分名称1: `テスト#${String(i).padStart(2, "0")}`,
        発生日時: dayjs().subtract(i, "minute").toISOString(),
        既読フラグ: 0,
        更新日時: dayjs().toISOString(),
      }))
      // 入力順に依存せず並ぶことを検証するため、あえて逆順(最古が先頭)で渡す
      showDakokuTenkoAlertsSorted([...list].reverse())
      console.info(`[TEST] 疑似アラート ${count} 件を発火。テスト#00(最新)が最上部なら正常。`)
    }

    // 表示中の疑似アラートを全消去（トースト破棄 + 表示済みID集合クリア）
    w.clearTestTenkoAlerts = () => {
      toast.dismiss()
      shownAlertIdsRef.current.clear()
      alertToastIdMapRef.current.clear()
      console.info("[TEST] トーストを全消去しました。")
    }

    console.info(
      "[TEST] 疑似アラートAPI有効: window.fireTestTenkoAlerts(20) / window.clearTestTenkoAlerts()",
    )

    return () => {
      delete w.fireTestTenkoAlerts
      delete w.clearTestTenkoAlerts
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Router basename={process.env.PUBLIC_URL ?? ""}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {isTablet ? (
          <TabletBody />
        ) : (
          <Switch>
            {/* TODO:ログイン画面など、認証外のページが存在する場合 AUth外に配置する */}
            <Route path={["/login"]} component={Login} />
            <Route path={["/settings"]} component={Settings} />
            <Auth>
              <Route path="/" component={Main} />
            </Auth>
          </Switch>
        )}
        <ToastContainer
          newestOnTop={true}
          closeOnClick={false}
          draggable={false}
          className="kairos-toast-container"
          toastClassName="kairos-toast"
        />
      </ErrorBoundary>
    </Router>
  )
}

export default App
function disPatch(arg0: AsyncThunkAction<{ infos: string[]; errors: string[]; warns: string[] }, any, {}>) {
  throw new Error("Function not implemented.")
}
